import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Message, Button, Confirm, Icon,
} from 'semantic-ui-react';

import LoadingDataEnhancer from './LoadingData';
import NotFound from '../views/NotFound';
import useFeatureFlag from '../hooks/useFeatureFlag';
import { DB_DEFAULT_COVERAGE } from '../constants/featureFlags';

import defaultCoverageMastersQuery from '../graphql/defaultCoverageMastersQuery';
import defaultCoverageEligibleProductsQuery from '../graphql/defaultCoverageEligibleProductsQuery';
import defaultCoverageFullyOptedOutCountQuery from '../graphql/defaultCoverageFullyOptedOutCountQuery';
import createDefaultCoverageMasterMutation from '../graphql/createDefaultCoverageMasterMutation';
import updateDefaultCoverageMasterMutation from '../graphql/updateDefaultCoverageMasterMutation';
import endDefaultCoverageMasterMutation from '../graphql/endDefaultCoverageMasterMutation';

import List from '../views/DefaultCoverageMasters/List';
import New from '../views/DefaultCoverageMasters/New';
import Edit from '../views/DefaultCoverageMasters/Edit';
import { ACTION } from '../views/DefaultCoverageMasters/ListRow';
import { formatDateOnly } from '../utilities/dateUtils';
import {
  MASTER_TYPE,
  NO_MASTERS_HEADER,
  NO_MASTERS_GUIDANCE,
  MASTERS_ERROR_HEADER,
  MASTERS_ERROR_GUIDANCE,
  toProductOptions,
  groupByPayerSide,
  hasNoMasters as computeHasNoMasters,
  hasMastersError as computeHasMastersError,
  isStaleVersionError,
} from '../constants/defaultCoverageMaster';

// Reads the three states of the masters query out of the single prop Apollo hands
// in. Exported and unit-tested for the same reason readMasterProductsState is on
// the opt-out side: loading, FAILED and came-back-empty all arrive as an empty
// list and must not read the same. Reporting a failed load as "nothing is
// configured" would send an operator off to create a master that may already
// exist — a duplicate, on a screen that creates charges.
export const readMastersState = data => {
  const loading = Boolean(data && data.loading);
  const failed = Boolean(data && data.error);
  const masters = (data && data.defaultCoverageMasters) || [];

  return {
    masters,
    mastersLoading: loading,
    hasNoMasters: computeHasNoMasters(data && data.defaultCoverageMasters, loading, failed),
    mastersError: computeHasMastersError(failed, loading),
  };
};

// The create payload. Split out because it is the whole of the mapping, and the
// field names are the thing that breaks silently: MockedProvider matches whatever
// the client sends, so a wrong name passes CI and fails in the browser.
export const buildCreateInput = values => ({
  defaultCoverageMaster: {
    masterType: values.masterType,
    productId: values.productId,
    priceDs: values.priceDs,
    startTs: values.startTs || null,
    endTs: values.endTs || null,
  },
});

// Version rides at the ROOT beside the id, not inside the nested input: it says
// which revision to act on, not what to change. Getting that nesting wrong is
// exactly what cost a deploy cycle on the opt-out screen.
//
// Dates are sent only when they CHANGED from what was loaded, read at the
// day granularity the form edits at. Absent means untouched (APMS COALESCEs),
// and re-sending an unchanged date is not harmless: the form only knows the day,
// so re-sending the start of a master created mid-day would move it to 00:00.
// Clearing an end date cannot be said with a null endTs (that reads as
// untouched), so it is `clearEndTs: true` with endTs omitted - tappy and APMS
// reject the two together. A cleared START has no equivalent flag, so blanking
// it on edit sends nothing and APMS keeps the stored start.
const loadedDay = ts => (ts ? formatDateOnly(moment(ts)) : null);

export const buildUpdateInput = (master, values) => {
  const defaultCoverageMaster = { priceDs: values.priceDs };
  const startTs = values.startTs || null;
  const endTs = values.endTs || null;

  if (startTs && startTs !== loadedDay(master.startTs)) defaultCoverageMaster.startTs = startTs;
  if (endTs) {
    if (endTs !== loadedDay(master.endTs)) defaultCoverageMaster.endTs = endTs;
  } else if (master.endTs) {
    defaultCoverageMaster.clearEndTs = true;
  }

  return {
    defaultCoverageMasterId: master.id,
    version: values.version !== null && values.version !== undefined ? values.version : master.version,
    defaultCoverageMaster,
  };
};

export const buildEndInput = master => ({
  defaultCoverageMasterId: master.id,
  version: master.version,
});

const DefaultCoverageMasters = ({
  data,
  eligibleProductsData,
  fullyOptedOutData,
  createMutation,
  updateMutation,
  endMutation,
}) => {
  const [isNewFormOpen, setIsNewFormOpen] = useState(false);
  // Tracked here, not just in Form's own state, because telling "nothing is
  // configured for this side" apart from "everything eligible is already taken"
  // needs the full masters list this container already has — see
  // hasAnyMasterOnSide below.
  const [newFormSide, setNewFormSide] = useState(MASTER_TYPE.BUYER);
  // Holds the just-created master so New's modal can stay open and show its
  // auto-populated rules for review/edit, instead of closing immediately. There is
  // no "create with these rules" call — APMS always seeds the template first — so
  // editing at creation time is really "create, then edit what it seeded."
  const [createdMaster, setCreatedMaster] = useState(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [pendingEnd, setPendingEnd] = useState(null);
  const [newFormError, setNewFormError] = useState(null);
  const [editFormError, setEditFormError] = useState(null);
  const [editStaleVersionError, setEditStaleVersionError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);

  const {
    masters, mastersLoading, hasNoMasters, mastersError,
  } = readMastersState(data);

  const productOptions = toProductOptions(
    eligibleProductsData && eligibleProductsData.defaultCoverageEligibleProducts
  );
  const productsLoading = Boolean(eligibleProductsData && eligibleProductsData.loading);
  const affectedOptedOutParties = (fullyOptedOutData
    && fullyOptedOutData.defaultCoverageFullyOptedOutCount) || 0;
  // An empty productOptions is ambiguous: this side could have every eligible
  // product already taken, or it could have never had anything on its
  // allowlist at all (v2: dynamic_properties.defaultMaster seeded empty for a
  // side, same as the opt-out screen's "no master products on this side" vs
  // "nothing seeded anywhere" split). Whether this side has ever carried a
  // master — from the list already loaded for the page, not a new query — is
  // what tells the two apart.
  const hasAnyMasterOnSide = groupByPayerSide(masters)[newFormSide].length > 0;

  const refetchAll = () => {
    if (data && data.refetch) data.refetch();
    if (fullyOptedOutData && fullyOptedOutData.refetch) fullyOptedOutData.refetch();
    if (eligibleProductsData && eligibleProductsData.refetch) eligibleProductsData.refetch();
  };

  // Eligibility and the affected-party count are BOTH per side, so switching the
  // radio has to re-ask for both. One product can carry a buyer master and a
  // seller master at once, and the two sides have different opt-out populations.
  const onMasterTypeChange = side => {
    setNewFormSide(side);
    if (eligibleProductsData && eligibleProductsData.refetch) {
      eligibleProductsData.refetch({ masterType: side });
    }
    if (fullyOptedOutData && fullyOptedOutData.refetch) {
      fullyOptedOutData.refetch({ masterType: side });
    }
  };

  const closeNewForm = () => {
    setIsNewFormOpen(false);
    setNewFormError(null);
    setCreatedMaster(null);
  };

  const closeEditForm = () => {
    setIsEditFormOpen(false);
    setSelectedMaster(null);
    setEditFormError(null);
    setEditStaleVersionError(null);
  };

  const onCreate = values => {
    setSubmitting(true);
    createMutation({ variables: { input: buildCreateInput(values) } })
      .then(({ data: response }) => {
        const { createDefaultCoverageMaster: { success, errors, defaultCoverageMaster } } = response;
        setSubmitting(false);
        if (success) {
          // A failed attempt before this one left its error up; it no longer applies.
          setNewFormError(null);
          setFlashMessage('Default coverage master created');
          refetchAll();
          // Stay open: the master now exists with APMS's auto-populated rules, and
          // the operator reviews/edits those before closing — see createdMaster above.
          setCreatedMaster(defaultCoverageMaster);
        } else {
          setNewFormError((errors && errors[0]) || 'Error while creating the master');
        }
      })
      .catch(() => {
        setSubmitting(false);
        setNewFormError('Error while creating the master');
      });
  };

  const onUpdate = values => {
    setSubmitting(true);
    setEditStaleVersionError(null);
    updateMutation({ variables: { input: buildUpdateInput(selectedMaster, values) } })
      .then(({ data: response }) => {
        const { updateDefaultCoverageMaster: { success, errors, errorCode } } = response;
        setSubmitting(false);
        if (success) {
          setFlashMessage('Default coverage master updated');
          refetchAll();
          closeEditForm();
          return;
        }
        const serverError = errors && errors[0];
        // A stale version is not a generic validation failure: the change never
        // landed and re-sending the same version never will, so it gets the loud
        // notice with the refresh action instead of the plain red box.
        if (isStaleVersionError(errorCode, serverError)) {
          setEditStaleVersionError(serverError);
        } else {
          setEditFormError(serverError || 'Error while updating the master');
        }
      })
      .catch(() => {
        setSubmitting(false);
        setEditFormError('Error while updating the master');
      });
  };

  const onEndConfirmed = () => {
    const master = pendingEnd;
    setPendingEnd(null);
    if (!master) return;

    endMutation({ variables: { input: buildEndInput(master) } })
      .then(({ data: response }) => {
        const { endDefaultCoverageMaster: { success, errors, errorCode } } = response;
        if (success) {
          setFlashMessage('Default coverage master ended');
          refetchAll();
        } else {
          const serverError = errors && errors[0];
          setFlashMessage(
            isStaleVersionError(errorCode, serverError)
              ? 'This master changed while you were looking at it — the list has been refreshed.'
              : serverError || 'Failed to end the master'
          );
          refetchAll();
        }
      })
      .catch(() => setFlashMessage('Failed to end the master'));
  };

  const actionHandler = (action, master) => {
    if (action === ACTION.EDIT) {
      setSelectedMaster(master);
      setIsEditFormOpen(true);
      return;
    }
    if (action === ACTION.END) setPendingEnd(master);
  };

  return (
    <div>
      {flashMessage && (
        <Message
          data-testid='default-coverage-master-flash'
          onDismiss={() => setFlashMessage(null)}
        >
          {flashMessage}
        </Message>
      )}

      {/* A failed load and an empty one both arrive as an empty list. Keeping
          them apart matters more here than on the opt-out screen: "nothing is
          configured" invites the operator to add a master, and doing that on a
          stale view is how a duplicate gets created. */}
      {mastersError && (
        <Message negative icon data-testid='default-coverage-masters-error'>
          <Icon name='exclamation triangle' />
          <Message.Content>
            <Message.Header>{MASTERS_ERROR_HEADER}</Message.Header>
            <p>{MASTERS_ERROR_GUIDANCE}</p>
          </Message.Content>
        </Message>
      )}
      {hasNoMasters && !mastersError && (
        <Message warning icon data-testid='default-coverage-masters-empty'>
          <Icon name='warning sign' />
          <Message.Content>
            <Message.Header>{NO_MASTERS_HEADER}</Message.Header>
            <p>{NO_MASTERS_GUIDANCE}</p>
          </Message.Content>
        </Message>
      )}

      <Button
        primary
        data-testid='default-coverage-master-add'
        onClick={() => {
          setIsNewFormOpen(true);
          onMasterTypeChange(MASTER_TYPE.BUYER);
        }}
        disabled={mastersError}
      >
        <Icon name='plus' /> Add Master
      </Button>

      <List defaultCoverageMasters={masters} actionHandler={actionHandler} />

      {/* Mounted only while open, like the opt-out screen's New: Form keeps its
          own side/product/price/date state, and a form left mounted after Cancel
          would reopen showing the last side picked while this container has just
          reset eligibility and the affected-party count to the buyer side. */}
      {isNewFormOpen && (
        <New
          isModalOpen={isNewFormOpen}
          onCloseHandler={closeNewForm}
          onSubmitHandler={onCreate}
          onMasterTypeChange={onMasterTypeChange}
          error={newFormError}
          submitting={submitting}
          productOptions={productOptions}
          productsLoading={productsLoading || mastersLoading}
          affectedOptedOutParties={affectedOptedOutParties}
          hasAnyMasterOnSide={hasAnyMasterOnSide}
          createdMaster={createdMaster}
        />
      )}

      <Edit
        isModalOpen={isEditFormOpen}
        onCloseHandler={closeEditForm}
        onSubmitHandler={onUpdate}
        onRefreshHandler={() => {
          refetchAll();
          closeEditForm();
        }}
        error={editFormError}
        staleVersionError={editStaleVersionError}
        submitting={submitting}
        defaultCoverageMaster={selectedMaster}
      />

      <Confirm
        open={Boolean(pendingEnd)}
        data-testid='default-coverage-master-end-confirm'
        header='End this default coverage master?'
        content={
          pendingEnd
            ? `Sales on this side will stop receiving ${pendingEnd.productCode || pendingEnd.productId} `
              + 'by default, and opt-out rows naming it become inert. The master is end-dated, not '
              + 'deleted, so the charges it produced still point at it.'
            : ''
        }
        onCancel={() => setPendingEnd(null)}
        onConfirm={onEndConfirmed}
        cancelButton={{ content: 'Cancel', 'data-testid': 'default-coverage-master-end-cancel' }}
        confirmButton={{ content: 'End master', 'data-testid': 'default-coverage-master-end-ok' }}
      />
    </div>
  );
};

DefaultCoverageMasters.propTypes = {
  data: PropTypes.shape({
    refetch: PropTypes.func,
    defaultCoverageMasters: PropTypes.array,
    error: PropTypes.object,
    loading: PropTypes.bool,
  }).isRequired,
  eligibleProductsData: PropTypes.shape({
    defaultCoverageEligibleProducts: PropTypes.array,
    refetch: PropTypes.func,
    loading: PropTypes.bool,
    error: PropTypes.object,
  }),
  fullyOptedOutData: PropTypes.shape({
    defaultCoverageFullyOptedOutCount: PropTypes.number,
    refetch: PropTypes.func,
    loading: PropTypes.bool,
    error: PropTypes.object,
  }),
  createMutation: PropTypes.func.isRequired,
  updateMutation: PropTypes.func.isRequired,
  endMutation: PropTypes.func.isRequired,
};

DefaultCoverageMasters.defaultProps = {
  eligibleProductsData: null,
  fullyOptedOutData: null,
};

// Unwrapped (no Apollo/router/flag HOCs), for component tests only.
export { DefaultCoverageMasters as DefaultCoverageMastersView };

const DefaultCoverageMastersContent = compose(
  withRouter,
  graphql(defaultCoverageMastersQuery, { options: { fetchPolicy: 'network-only' } }),
  graphql(defaultCoverageEligibleProductsQuery, {
    name: 'eligibleProductsData',
    options: { variables: { masterType: MASTER_TYPE.BUYER }, fetchPolicy: 'network-only' },
  }),
  graphql(defaultCoverageFullyOptedOutCountQuery, {
    name: 'fullyOptedOutData',
    options: { variables: { masterType: MASTER_TYPE.BUYER }, fetchPolicy: 'network-only' },
  }),
  graphql(createDefaultCoverageMasterMutation, { name: 'createMutation' }),
  graphql(updateDefaultCoverageMasterMutation, { name: 'updateMutation' }),
  graphql(endDefaultCoverageMasterMutation, { name: 'endMutation' }),
  LoadingDataEnhancer
)(DefaultCoverageMasters);

// Same DB feature flag as the opt-out screen: the two are one feature, and
// shipping the masters screen without the opt-out screen (or the reverse) would
// give Ops half a story. The gate sits outside the Apollo enhancers so no query
// is issued while the flag is off. Role access is enforced by the authorize()
// HOC wrapping this component in App.js.
const DefaultCoverageMastersPage = () => {
  const [isEnabled, loaded] = useFeatureFlag(DB_DEFAULT_COVERAGE);

  if (!loaded) return null;
  if (!isEnabled) return <NotFound />;

  return <DefaultCoverageMastersContent />;
};

export default DefaultCoverageMastersPage;
